import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Members ─────────────────────────────────────────────

/** Get team members for a project */
export async function getMembers(projectId) {
  const { data } = await axios.get(`${API}/team/members/${projectId}`);
  return data;
}

/** Invite a new member to a project */
export async function inviteMember(inviteData) {
  const { data } = await axios.post(`${API}/team/invite`, inviteData);
  return data;
}

/** Update a member's role */
export async function updateMemberRole(memberId, role) {
  const { data } = await axios.put(`${API}/team/members/${memberId}/role`, { role });
  return data;
}

/** Update a member's permissions — accepts full permissions object */
export async function updateMemberPermissions(memberId, permissions) {
  const { data } = await axios.put(`${API}/team/members/${memberId}/permissions`, { permissions });
  return data;
}

/** Remove a member from a project */
export async function removeMember(memberId) {
  const { data } = await axios.delete(`${API}/team/members/${memberId}`);
  return data;
}

// ─── Invites ─────────────────────────────────────────────

/** Get pending invites for the current user */
export async function getMyInvites() {
  const { data } = await axios.get(`${API}/team/my-invites`);
  return data;
}

/** Get invite details by token */
export async function getInviteInfo(token) {
  const { data } = await axios.get(`${API}/team/invite-info/${token}`);
  return data;
}

/** Accept a team invitation */
export async function acceptInvite(token) {
  const { data } = await axios.post(`${API}/team/accept-invite/${token}`, {});
  return data;
}

// ─── Comments ────────────────────────────────────────────

/** Get comments for a project */
export async function getComments(projectId) {
  const { data } = await axios.get(`${API}/team/comments/${projectId}`);
  return data;
}

/** Create a new comment */
export async function createComment(commentData) {
  const { data } = await axios.post(`${API}/team/comments`, commentData);
  return data;
}

/** Resolve a comment */
export async function resolveComment(commentId) {
  const { data } = await axios.put(`${API}/team/comments/${commentId}`, { resolved: true });
  return data;
}

/** Delete a comment */
export async function deleteComment(commentId) {
  const { data } = await axios.delete(`${API}/team/comments/${commentId}`);
  return data;
}

// ─── Notifications ──────────────────────────────────────

/** Get notifications for the current user */
export async function getMyNotifications(limit = 50, offset = 0) {
  const { data } = await axios.get(`${API}/team/my-notifications`, { params: { limit, offset } });
  return data;
}

/** Get unread notification count */
export async function getUnreadCount() {
  const { data } = await axios.get(`${API}/team/notifications/unread-count`);
  return data.count;
}

/** Mark a single notification as read */
export async function markNotificationRead(notificationId) {
  const { data } = await axios.put(`${API}/team/notifications/${notificationId}/read`);
  return data;
}

/** Mark all notifications as read */
export async function markAllNotificationsRead() {
  const { data } = await axios.put(`${API}/team/notifications/read-all`);
  return data;
}

/** Delete a notification */
export async function deleteNotification(notificationId) {
  const { data } = await axios.delete(`${API}/team/notifications/${notificationId}`);
  return data;
}

/** Decline an invitation */
export async function declineInvite(token) {
  const { data } = await axios.post(`${API}/team/decline-invite/${token}`, {});
  return data;
}

// ─── Activity & Permissions ──────────────────────────────

/** Get activity feed for a project */
export async function getActivity(projectId) {
  const { data } = await axios.get(`${API}/team/activity/${projectId}`);
  return data;
}

/** Get RBAC configuration */
export async function getRbacConfig() {
  const { data } = await axios.get(`${API}/team/rbac-config`);
  return data;
}

/** Get current user's permissions for a project */
export async function getMyPermissions(projectId) {
  const { data } = await axios.get(`${API}/team/my-permissions/${projectId}`);
  return data;
}
